// Roadmap feedback lives in the Nautilus feedback inbox (/api/feedback-d1, screenshots in
// /api/feedback-screenshot), tagged so the roadmap panel can list its own entries while
// they still show up — and get triaged — in the app-wide Feedback page.
const TAG = 'Program Roadmap';
const TAG_PATTERN = /^\[Program Roadmap(?: · ([^\]]*))?\]\s*([\s\S]*)$/;
const screenshotIds = new Map();

const request = async (path, { method = 'GET', body } = {}) => {
    const response = await fetch(path, {
        method,
        headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
            ...(body ? { 'Content-Type': 'application/json' } : {})
        },
        body: body ? JSON.stringify(body) : undefined
    });
    if (!response.ok) throw new Error(`${method} ${path} -> ${response.status}`);
    return response.json();
};

const feedbackId = () => `roadmap-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Forge `submitFeedback` / `listFeedback` / `resolveFeedback` / `deleteFeedback` /
 * `getFeedbackImage` resolvers, backed by the Nautilus feedback API.
 * @param {{ user: () => {name: string, accountId: string}, t: (key: string) => string }} deps
 */
export const createFeedbackResolvers = ({ user, t }) => ({
    async submitFeedback({ kind, message, image, roadmap }) {
        const text = String(message || '').trim().slice(0, 2000);
        if (!text) throw new Error(t('roadmap.feedback.emptyMessage'));
        const id = feedbackId();
        let screenshotUrl = '';
        if (/^data:image\//.test(String(image || ''))) {
            screenshotUrl = (await request('/api/feedback-screenshot', { method: 'POST', body: { feedbackId: id, data: image } })).screenshotId || '';
        }
        const title = String(roadmap || '').replace(/[\[\]]/g, '').slice(0, 120);
        await request('/api/feedback-d1', {
            method: 'POST',
            body: {
                id,
                type: kind === 'bug' ? 'bug' : 'idea',
                description: `[${TAG}${title ? ` · ${title}` : ''}] ${text}`,
                screenshotUrl,
                status: 'open',
                createdAt: new Date().toISOString()
            }
        });
        return { ok: true };
    },

    async listFeedback() {
        const me = user();
        const { items = [] } = await request('/api/feedback-d1?limit=1000');
        const entries = items.flatMap((row) => {
            const match = TAG_PATTERN.exec(String(row.description || ''));
            if (!match || !match[2].trim()) return [];
            const own = Boolean(me.accountId) && row.created_by === me.accountId;
            if (row.screenshot_url) screenshotIds.set(String(row.id), row.screenshot_url);
            const resolved = row.status === 'done';
            return [{
                id: String(row.id),
                at: row.created_at || '',
                kind: row.type === 'bug' ? 'bug' : 'idea',
                message: match[2].trim(),
                by: own ? me.name : '',
                roadmap: match[1] || '',
                hasImage: Boolean(row.screenshot_url),
                resolved,
                resolvedAt: resolved ? row.resolved_at || '' : '',
                resolvedBy: '',
                // Same rules as the Nautilus Feedback page: anyone triages, authors delete.
                canResolve: true,
                canDelete: own
            }];
        }).sort((left, right) => Date.parse(right.at || 0) - Date.parse(left.at || 0));
        const resolvedCount = entries.filter((entry) => entry.resolved).length;
        return { items: entries, count: entries.length, openCount: entries.length - resolvedCount, resolvedCount };
    },

    async resolveFeedback({ id, resolved }) {
        await request('/api/feedback-d1', { method: 'PUT', body: { id: String(id), status: resolved ? 'done' : 'open' } });
        return { ok: true, resolved: Boolean(resolved) };
    },

    async deleteFeedback({ id }) {
        const me = user();
        const { items = [] } = await request('/api/feedback-d1?limit=1000');
        const row = items.find((entry) => String(entry.id) === String(id));
        if (row && row.created_by !== me.accountId) throw new Error(t('roadmap.feedback.ownOnly'));
        await request('/api/feedback-d1', { method: 'DELETE', body: { id: String(id) } });
        if (row?.screenshot_url) {
            await request('/api/feedback-screenshot', { method: 'DELETE', body: { screenshotId: row.screenshot_url } }).catch(() => {});
        }
        screenshotIds.delete(String(id));
        return { ok: true };
    },

    async getFeedbackImage({ id }) {
        const screenshotId = screenshotIds.get(String(id));
        if (!screenshotId) return { image: '' };
        const { data } = await request(`/api/feedback-screenshot?id=${encodeURIComponent(screenshotId)}`);
        return { image: typeof data === 'string' ? data : '' };
    }
});
